import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Select,
  Text,
} from '@chakra-ui/react';
import type { OnChangeFn, PaginationState } from '@tanstack/react-table';
import { format as d3Format } from 'd3-format';
import { useMemo } from 'react';

export interface TablePaginationProps {
  pagination: PaginationState;
  setPagination: OnChangeFn<PaginationState>;
  totalItems: number;
  pageSizeOptions?: number[];
  hidePageSizeSelector?: boolean;
}

export const TablePagination = ({
  pagination,
  setPagination,
  totalItems,
  pageSizeOptions = [10, 20, 50, 100],
  hidePageSizeSelector = false,
}: TablePaginationProps) => {
  const pageCount = Math.max(1, Math.ceil(totalItems / pagination.pageSize));

  const displayPages = useMemo<(number | 'ellipsis')[]>(() => {
    if (pageCount <= 5) {
      return Array.from({ length: pageCount }, (_, i) => i + 1);
    }

    const current = pagination.pageIndex + 1; // 1-indexed current page
    const tentativeStart = Math.max(2, current - 1);
    const tentativeEnd = Math.min(pageCount - 1, current + 1);

    // guarantee window contains 3 pages
    const windowStart =
      tentativeEnd - tentativeStart < 2
        ? Math.max(2, Math.min(tentativeStart, pageCount - 3))
        : tentativeStart;
    const windowEnd = windowStart + 2;

    const middlePages = Array.from(
      { length: windowEnd - windowStart + 1 },
      (_, i) => windowStart + i
    );

    const result: (number | 'ellipsis')[] = [
      1,
      ...(windowStart > 2 ? (['ellipsis'] as const) : []),
      ...middlePages,
      ...(windowEnd < pageCount - 1 ? (['ellipsis'] as const) : []),
      pageCount,
    ];
    return result;
  }, [pageCount, pagination.pageIndex]);

  return (
    <Flex
      justify="center"
      align="center"
      py={2}
      gap={2}
      onClick={(e) => e.stopPropagation()}
    >
      <HStack spacing={2} align="center">
        <IconButton
          aria-label="Previous Page"
          icon={<ChevronLeftIcon />}
          size="sm"
          variant="ghost"
          onClick={() =>
            setPagination((p) => ({
              ...p,
              pageIndex: Math.max(0, p.pageIndex - 1),
            }))
          }
          isDisabled={pagination.pageIndex === 0}
        />
        {displayPages.map((pg, idx) => {
          if (pg === 'ellipsis') {
            return <Box key={`ellipsis-${idx}`}>…</Box>;
          }
          const isActive = pagination.pageIndex === pg - 1;
          return (
            <Button
              key={`page-btn-${idx}`}
              size="sm"
              aria-current={isActive ? 'page' : undefined}
              variant={isActive ? 'solid' : 'ghost'}
              bg={isActive ? '#82fba9' : undefined}
              _hover={{ bg: isActive ? '#82fba9' : 'gray.200' }}
              _active={{ bg: '#82fba9' }}
              onClick={() =>
                setPagination((p) => ({ ...p, pageIndex: pg - 1 }))
              }
            >
              <Text>{d3Format(',')(pg)}</Text>
            </Button>
          );
        })}
        <IconButton
          aria-label="Next Page"
          icon={<ChevronRightIcon />}
          size="sm"
          variant="ghost"
          onClick={() =>
            setPagination((p) => ({
              ...p,
              pageIndex: Math.min(pageCount - 1, p.pageIndex + 1),
            }))
          }
          isDisabled={pagination.pageIndex >= pageCount - 1}
        />
      </HStack>

      {!hidePageSizeSelector && (
        <Select
          width="auto"
          size="sm"
          value={String(pagination.pageSize)}
          onChange={(e) =>
            setPagination((prev) => ({
              ...prev,
              pageSize: Number(e.target.value),
              pageIndex: 0,
            }))
          }
          borderRadius="md"
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={String(size)}>
              Show {size}
            </option>
          ))}
        </Select>
      )}
    </Flex>
  );
};
